import * as React from 'react'
import { observer } from 'mobx-react'

import Icon, {
    ApartmentOutlined,
    DeploymentUnitOutlined,
    DollarOutlined,
    FileSearchOutlined,
    FlagOutlined,
    GlobalOutlined,
    LockFilled,
    LogoutOutlined,
    MessageOutlined,
    TagFilled,
    TeamOutlined,
} from '@ant-design/icons'

import { Menu } from 'antd'
import { Sidebar as AqeSB } from '@pushly/aqe/lib/components'
import { AppState } from '@halo/stores/app.state'
import { AppService } from '@halo/services/app.service'
import { preventDefault } from '@pushly/aqe/lib/utils/prevent'
import { AuthService } from '@halo/services/auth.service'
import { PushlyIcon } from '@halo/components/icons/pushly-icon'
import { classnames } from '@halo/_utils/classnames'
import { MenuInfo } from '@rc-component/menu/lib/interface'
import aqe from '@pushly/aqe'
import { AccessRoleId } from '@halo/enums/access-role.enum'
import { useUserPreferences } from '@pushly/aqe/lib/providers/user-preferences-provider/core'
import { useService } from '@pushly/aqe/lib/hooks/index'

const UserMenu = AqeSB.UserMenu

interface IMenuItem {
    key: string
    path?: string
    external?: boolean
    icon: React.ReactElement
    title: string
    condition?: () => boolean
    sub?: IMenuItem[]
}

export const Sidebar = observer(() => {
    const appState = useService(AppState)
    const appService = useService(AppService)
    const authService = useService(AuthService)
    const userPreferences = useUserPreferences(appState.currentUser?.id)

    const isActingUserInternal = React.useCallback((): boolean => {
        return appState.currentUser?.isInternalUser ?? false
    }, [appState.currentUser])

    const isActingUserInternalDev = React.useCallback((): boolean => {
        return appState.currentUser?.isInternalDevUser ?? false
    }, [appState.currentUser])

    const currentUserCanAccessBatchBilling = React.useCallback(() => {
        return (
            isActingUserInternal() &&
            [
                AccessRoleId.INTERNAL_DEVELOPER,
                AccessRoleId.INTERNAL_ACCOUNT_MANAGER_LEAD,
                AccessRoleId.INTERNAL_ACCOUNT_MANAGER,
            ].includes(appState.currentUser?.user_role_id)
        )
    }, [appState.currentUser, isActingUserInternal])

    const currentUserCanAccessPlatformMessages = React.useCallback((): boolean => {
        return (
            isActingUserInternal() &&
            [AccessRoleId.INTERNAL_DEVELOPER, AccessRoleId.INTERNAL_ACCOUNT_MANAGER_LEAD].includes(
                appState.currentUser?.user_role_id,
            )
        )
    }, [appState.currentUser, isActingUserInternal])

    const routes = React.useMemo((): IMenuItem[] => {
        return [
            {
                key: 'orgs',
                path: '/organizations',
                icon: <ApartmentOutlined />,
                title: 'Organizations',
                condition: appService.currentUserCanReadAtLeastOneOrg,
            },
            {
                key: 'domains',
                path: '/domains',
                icon: <GlobalOutlined />,
                title: 'Domains',
                condition: isActingUserInternal,
            },
            {
                key: 'users',
                path: '/users',
                icon: <TeamOutlined />,
                title: 'Users',
                condition: isActingUserInternal,
            },
            {
                key: 'batch-billing',
                path: '/batch-billing',
                icon: <DollarOutlined />,
                title: 'Batch Billing',
                condition: currentUserCanAccessBatchBilling,
            },
            {
                key: 'page_analysis_configs',
                path: '/page-analysis-configs',
                icon: <FileSearchOutlined />,
                title: 'Page Analysis Configs',
                condition: isActingUserInternalDev,
            },
            {
                key: 'jobs',
                path: '/dev/jobs',
                icon: <DeploymentUnitOutlined />,
                title: 'Jobs',
                condition: isActingUserInternalDev,
            },
            {
                key: 'flags',
                path: '/flags',
                icon: <FlagOutlined />,
                title: 'Flags',
                condition: isActingUserInternalDev,
            },
            {
                key: 'access-control',
                path: '/access-control',
                icon: <LockFilled />,
                title: 'Access Control',
                condition: isActingUserInternalDev,
            },
            {
                key: 'white-labels',
                path: '/white-labels',
                icon: <TagFilled />,
                title: 'White-Labels',
                condition: isActingUserInternalDev,
            },
            {
                key: 'messages',
                path: '/platform-messages',
                icon: <MessageOutlined />,
                title: 'Messages',
                condition: currentUserCanAccessPlatformMessages,
            },
            // Last item should always be Public Platform Link
            {
                key: 'pub-plat',
                path: aqe.defaults.publicPlatformDomain,
                external: true,
                icon: <Icon component={PushlyIcon} />,
                title: 'Public Platform',
            },
        ]
    }, [
        appService.currentUserCanReadAtLeastOneOrg,
        isActingUserInternal,
        isActingUserInternalDev,
        currentUserCanAccessBatchBilling,
        currentUserCanAccessPlatformMessages,
    ])

    const currentlySelectedKeys = React.useMemo((): string[] => {
        const currentPath = appService.currentRoute
        const openRoutes = routes.filter(
            (r) => r.path === currentPath || (r.path !== '/' && new RegExp(`^${r.path}`, 'i').test(currentPath)),
        )

        return openRoutes.map((r) => r.key)
    }, [appService.currentRoute, routes])

    const handleMenuClick = React.useCallback(
        async ({ key, domEvent }: MenuInfo) => {
            preventDefault(domEvent)

            const currentPath = appService.currentRoute
            const routeItem = routes.find((r) => r.key === key)
            const newPath = routeItem.path
            const isExternal = routeItem.external ?? false

            if (domEvent.metaKey) {
                window.open(newPath)
            } else if (isExternal) {
                window.open(newPath, newPath)
            } else if (currentPath !== newPath) {
                appService.route(newPath)
            }
        },
        [appService, routes],
    )

    const handleLogout = React.useCallback(
        async (ev: any) => {
            preventDefault(ev)
            return authService.logout()
        },
        [authService],
    )

    const sidebarExpanded = userPreferences.get(['sidebar_expanded']) ?? true

    return (
        <AqeSB
            collapsed={!sidebarExpanded}
            onToggle={(collapsed) =>
                collapsed
                    ? userPreferences.set(['sidebar_expanded'], false)
                    : userPreferences.set(['sidebar_expanded'], true)
            }
        >
            <AqeSB.Header>
                {/* TODO: Update AqeSB.AppIcon component */}
                <div className={classnames('app-icon')}>
                    <span
                        onClick={() => {
                            appService.route('/')
                        }}
                    >
                        <Icon component={PushlyIcon} />
                    </span>
                </div>
                {/*<AqeSB.AppIcon*/}
                {/*    onClick={() => {*/}
                {/*        appService.route('/');*/}
                {/*    }}*/}
                {/*/>*/}
            </AqeSB.Header>
            <AqeSB.Content>
                <Menu
                    mode="inline"
                    inlineCollapsed={!sidebarExpanded}
                    subMenuCloseDelay={0}
                    onClick={handleMenuClick}
                    selectedKeys={currentlySelectedKeys}
                >
                    {routes
                        .filter((r) => !r.condition || r.condition())
                        .map((r) =>
                            r.sub ? (
                                <Menu.SubMenu key={r.key} title={r.title}>
                                    {r.sub.map((sr) => (
                                        <Menu.Item
                                            key={sr.key}
                                            data-path={sr.path}
                                            data-external={sr.external === true}
                                            icon={sr.icon}
                                        >
                                            <span>{sr.title}</span>
                                        </Menu.Item>
                                    ))}
                                </Menu.SubMenu>
                            ) : (
                                <Menu.Item
                                    key={r.key}
                                    data-path={r.path}
                                    data-external={r.external === true}
                                    icon={r.icon}
                                >
                                    <span>{r.title}</span>
                                </Menu.Item>
                            ),
                        )}
                </Menu>
            </AqeSB.Content>
            <AqeSB.Footer>
                {!!appState.currentUser && (
                    <UserMenu user={appState.currentUser}>
                        <UserMenu.Action>
                            <a onClick={handleLogout}>
                                <LogoutOutlined />
                                Logout
                            </a>
                        </UserMenu.Action>
                    </UserMenu>
                )}
            </AqeSB.Footer>
        </AqeSB>
    )
})
